import * as Sentry from '@sentry/react';

/**
 * Sentry configuration options
 */
export interface SentryConfig {
  dsn: string;
  environment: string;
  tracesSampleRate?: number;
  enabled?: boolean;
}

/**
 * Initialize Sentry for error tracking
 */
export function initSentry(config?: Partial<SentryConfig>) {
  const dsn = config?.dsn || import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    console.warn('Sentry DSN not provided. Sentry monitoring disabled.');
    return;
  }

  const environment = config?.environment || import.meta.env.MODE || 'development';
  const enabled = config?.enabled !== false && environment !== 'development';

  if (!enabled) {
    console.log('Sentry disabled in development mode');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    tracesSampleRate: config?.tracesSampleRate ?? 0.1,
    
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException as Error;
        if (error?.message?.includes('extension') || error?.message?.includes('chrome-extension')) {
          return null;
        }
      }
      return event;
    },
  });

  console.log('Sentry initialized successfully');
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(userId: string, email?: string, username?: string) {
  Sentry.setUser({ id: userId, email, username });
}

/**
 * Clear user context (on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null);
}

/**
 * Add custom tags to error reports
 */
export function setSentryTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

/**
 * Add custom context to error reports
 */
export function setSentryContext(key: string, context: Record<string, any>) {
  Sentry.setContext(key, context);
}

/**
 * Track a message for debugging
 */
export function addSentryBreadcrumb(message: string, data?: Record<string, any>, level: Sentry.SeverityLevel = 'info') {
  Sentry.addBreadcrumb({ message, data, level });
}

/**
 * Capture exception manually
 */
export function captureSentryException(error: Error, context?: { tags?: Record<string, any>; extra?: Record<string, any> }) {
  Sentry.captureException(error, { tags: context?.tags, extra: context?.extra });
}

/**
 * Capture message
 */
export function captureSentryMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}
